<template>
  <div v-if="showModal" :class="$utilModalStyle.modalOverlay" @click="closeModal">
    <div :class="[$utilModalStyle.modalContent, $style.modalContentLarge]" @click.stop>
      <div :class="$style.modalHeader">
        <button :class="$style.backButton" @click="handleBack">
          <img :src="backIcon" :alt="$t('back')" :class="$style.backIcon" />
        </button>
        <h3 :class="$style.modalTitle">{{ $t("teacher-application-detail-title") }}</h3>
        <button :class="$style.closeButton" @click="closeModal">
          <img :src="closeIcon" :alt="$t('close')" :class="$style.closeIcon" />
        </button>
      </div>
      <div :class="$style.modalBody">
        <div v-if="loading" :class="$style.loading">{{ $t("teacher-application-detail-loading") }}</div>
        <div v-else-if="error" :class="$style.error">{{ error }}</div>
        <div v-else-if="application" :class="$style.applicationDetails">
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("first-name") }}:</span>
            <span :class="$style.value">{{ application.firstName }}</span>
          </div>
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("last-name") }}:</span>
            <span :class="$style.value">{{ application.lastName }}</span>
          </div>
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("email") }}:</span>
            <span :class="$style.value">{{ application.email }}</span>
          </div>
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("teacher-application-date-of-birth") }}:</span>
            <span :class="$style.value">{{ application.dateOfBirth }}</span>
          </div>
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("teacher-application-phone-number") }}:</span>
            <span :class="$style.value">{{ application.phoneNumber }}</span>
          </div>
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("teacher-application-institution") }}:</span>
            <span :class="$style.value">{{ application.institution }}</span>
          </div>
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("teacher-application-position-role") }}:</span>
            <span :class="$style.value">{{ application.positionRole }}</span>
          </div>
          <div v-if="application.description" :class="$style.detailRow">
            <span :class="$style.label">{{ $t("short-description") }}:</span>
            <span :class="$style.value">{{ application.description }}</span>
          </div>
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("teacher-application-status") }}:</span>
            <span :class="[$style.value, getStatusClass(application.status)]">{{ statusLabel(application.status) }}</span>
          </div>
          <div :class="$style.detailRow">
            <span :class="$style.label">{{ $t("teacher-application-submitted") }}:</span>
            <span :class="$style.value">{{ application.createdAt }}</span>
          </div>
          
          <div v-if="application.status === 'pending'" :class="$style.actionSection">
            <div :class="$style.messageSection">
              <label :class="$style.messageLabel">{{ $t("teacher-application-optional-message") }}:</label>
              <textarea
                v-model="message"
                :class="$style.messageInput"
                :placeholder="$t('teacher-application-optional-message-placeholder')"
                rows="3"
              ></textarea>
              <div :class="$style.templateControls">
                <select :class="$style.templateSelect" @change="applyTemplate($event)">
                  <option value="">Shared template</option>
                  <option
                    v-for="template in sharedTemplates"
                    :key="`shared-${template.id}`"
                    :value="template.id"
                  >
                    {{ template.name || templatePreview(template.message) }}
                  </option>
                </select>
                <select :class="$style.templateSelect" @change="applyTemplate($event)">
                  <option value="">My template</option>
                  <option
                    v-for="template in myTemplates"
                    :key="`mine-${template.id}`"
                    :value="template.id"
                  >
                    {{ template.name || templatePreview(template.message) }}
                  </option>
                </select>
              </div>
            </div>
            <div :class="$style.actionButtons">
              <UtilButton
                :text="$t('teacher-application-accept')"
                :clickFunction="handleAccept"
                size="md"
                backBg="#28a745"
                frontBg="#34ce57"
                textColor="#FFFFFF"
                buttonWidth="150px"
                :disabled="processing"
              />
              <UtilButton
                :text="$t('teacher-application-decline')"
                :clickFunction="handleDecline"
                size="md"
                backBg="#dc3545"
                frontBg="#e4606d"
                textColor="#FFFFFF"
                buttonWidth="150px"
                :disabled="processing"
              />
            </div>
          </div>
          <div v-else :class="$style.reviewedSection">
            <div v-if="application.adminNotes" :class="$style.detailRow">
              <span :class="$style.label">{{ $t("teacher-application-admin-notes") }}:</span>
              <span :class="$style.value">{{ application.adminNotes }}</span>
            </div>
            <div :class="$style.reviewedMessage">
              {{ $t("teacher-application-reviewed-message", { status: statusLabel(application.status) }) }}
            </div>
          </div>
        </div>
      </div>
      <div :class="$style.modalFooter">
        <button :class="$style.closeBtn" @click="closeModal">{{ $t("close") }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import utilModalStyles from "@/css/UtilModal.module.css";
import styles from "@/css/TeacherApplicationDetailModal.module.css";
import CloseIcon from "@/assets/icons/close-red.svg";
import BackIcon from "@/assets/icons/left-arrow.svg";
import UtilButton from "@/components/util/UtilButton.vue";
import gameService from "@/services/game.service";
import { toast } from "vue3-toastify";

export default {
  name: "TeacherApplicationDetailModal",
  components: { UtilButton },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
    applicationId: {
      type: Number,
      default: null,
      validator: (value) => value === null || typeof value === 'number',
    },
    onApplicationUpdated: {
      type: Function,
      default: null,
    },
    onBack: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      application: null,
      loading: false,
      error: null,
      message: "",
      myTemplates: [],
      sharedTemplates: [],
      processing: false,
      closeIcon: CloseIcon,
      backIcon: BackIcon,
    };
  },
  computed: {
    $utilModalStyle() {
      return utilModalStyles;
    },
    $style() {
      return styles;
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal && this.applicationId) {
        this.fetchApplicationDetails();
        this.fetchTemplates();
      } else {
        this.application = null;
        this.error = null;
        this.message = "";
      }
    },
  },
  methods: {
    handleBack() {
      if (this.onBack) {
        this.onBack();
      } else {
        this.closeModal();
      }
    },
    statusLabel(status) {
      return this.$t(`teacher-application-status-${status || "pending"}`);
    },
    async fetchApplicationDetails() {
      if (!this.applicationId) {
        this.error = this.$t("teacher-application-no-id");
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const res = await gameService.getTeacherApplicationDetail(this.applicationId);
        if (res && res.success && res.application) {
          this.application = res.application;
        } else {
          this.error = res?.message || this.$t("teacher-application-detail-fetch-failed");
          toast.error(this.error);
        }
      } catch (err) {
        this.error = this.$t("teacher-application-detail-fetch-error");
        toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    async fetchTemplates() {
      try {
        const res = await gameService.getReviewTemplates();
        if (res && res.success) {
          this.myTemplates = res.myTemplates || [];
          this.sharedTemplates = res.sharedTemplates || [];
        }
      } catch (err) {
        console.error("Failed to fetch review templates:", err);
      }
    },
    templatePreview(message) {
      const text = (message || "").replace(/\s+/g, " ").trim();
      return text.length > 70 ? `${text.slice(0, 70)}...` : text;
    },
    applyTemplate(event) {
      const templateId = Number(event.target.value);
      if (!templateId) return;
      const template = [...this.myTemplates, ...this.sharedTemplates].find((item) => item.id === templateId);
      if (template) this.message = template.message;
      event.target.value = "";
    },
    async handleAccept() {
      if (this.processing) return;
      
      this.processing = true;
      try {
        const res = await gameService.acceptTeacherApplication(
          this.applicationId,
          this.message || null
        );
        if (res && res.success) {
          toast.success(this.$t("teacher-application-accepted-success"));
          if (this.onApplicationUpdated) {
            this.onApplicationUpdated();
          }
          this.closeModal();
        } else {
          toast.error(res?.message || this.$t("teacher-application-accept-failed"));
        }
      } catch (err) {
        toast.error(this.$t("teacher-application-accept-error"));
      } finally {
        this.processing = false;
      }
    },
    async handleDecline() {
      if (this.processing) return;
      
      this.processing = true;
      try {
        const res = await gameService.declineTeacherApplication(
          this.applicationId,
          this.message || null
        );
        if (res && res.success) {
          toast.success(this.$t("teacher-application-declined-success"));
          if (this.onApplicationUpdated) {
            this.onApplicationUpdated();
          }
          this.closeModal();
        } else {
          toast.error(res?.message || this.$t("teacher-application-decline-failed"));
        }
      } catch (err) {
        toast.error(this.$t("teacher-application-decline-error"));
      } finally {
        this.processing = false;
      }
    },
    getStatusClass(status) {
      if (status === "accepted") return this.$style.statusAccepted;
      if (status === "declined") return this.$style.statusDeclined;
      return this.$style.statusPending;
    },
  },
};
</script>

<style module>
</style>
